import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import { useAuth } from '../../AuthProvider'
import './MFASetup.css'

// The key an authenticator app asks for when the QR code is not scanned: shown in groups
// of four so a person can read it across and type it without losing their place.
function groupSecret(secret) {
  return (secret || '').replace(/\s+/g, '').match(/.{1,4}/g)?.join(' ') || ''
}

export default function MFASetup() {
  const navigate = useNavigate()
  const { isAuthenticated, enrollMFA, confirmMFAEnrollment, unenrollMFA, getMFAStatus, checkMFAEnrollment, getToken, needsMFASetup } = useAuth()

  const [status, setStatus] = useState(null) // { enrolled, factors }
  const [enrolling, setEnrolling] = useState(false)
  const [enrollment, setEnrollment] = useState(null) // { factorId, qr, secret }
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(true)
  const [removing, setRemoving] = useState(false)
  const [qrFailed, setQrFailed] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!isAuthenticated) return
    getMFAStatus().then(s => {
      setStatus(s)
      setLoading(false)
    })
  }, [isAuthenticated, getMFAStatus])

  async function handleEnroll() {
    setEnrolling(true)
    setError('')
    const result = await enrollMFA()
    if (result.success) {
      setQrFailed(false)
      setCopied(false)
      setEnrollment(result)
    } else {
      setError(result.error || 'Failed to start enrollment.')
    }
    setEnrolling(false)
  }

  async function copySecret() {
    try {
      await navigator.clipboard.writeText(enrollment.secret)
      setCopied(true)
    } catch {
      setCopied(false) // clipboard blocked: the key stays selectable on the page
    }
  }

  async function handleVerify(e) {
    e.preventDefault()
    if (code.length !== 6) return
    setLoading(true)
    setError('')
    const result = await confirmMFAEnrollment(enrollment.factorId, code)
    if (result.success) {
      setSuccess('Two-factor authentication enabled! Redirecting…')
      setEnrollment(null)
      setCode('')
      // Refresh MFA status in auth context + refresh status card
      const token = getToken && getToken()
      if (checkMFAEnrollment && token) {
        await checkMFAEnrollment(token)
      }
      const s = await getMFAStatus()
      setStatus(s)
      // Redirect to home after a brief pause
      setTimeout(() => navigate('/home'), 1200)
    } else {
      setError(result.error || 'Invalid code. Please try again.')
      setCode('')
    }
    setLoading(false)
  }

  async function handleRemove(factorId) {
    if (!confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) return
    setRemoving(true)
    setError('')
    const result = await unenrollMFA(factorId)
    if (result.success) {
      setSuccess('Two-factor authentication has been disabled.')
      const s = await getMFAStatus()
      setStatus(s)
    } else {
      setError(result.error || 'Failed to disable MFA.')
    }
    setRemoving(false)
  }

  const breadcrumbs = [
    { label: 'Home', href: '/home' },
    { label: 'Security' },
  ]

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs}
        actions={<button className="nav-btn" onClick={() => navigate('/home')}>Back to Home</button>}
      />
      <div className="mfa-page">
        <div className="mfa-container">
          <h1 className="mfa-title">Two-Factor Authentication</h1>
          <p className="mfa-subtitle">
            Add an extra layer of security to your account using a TOTP authenticator app
            like Google Authenticator, Authy, or 1Password.
          </p>

          {error && <div className="mfa-alert mfa-alert-error">{error}</div>}
          {success && <div className="mfa-alert mfa-alert-success">{success}</div>}
          {needsMFASetup && !success && (
            <div className="mfa-alert mfa-alert-warning">
              Two-factor authentication is required for your account.
              Set it up to continue using Sempirical.
            </div>
          )}

          {loading && !enrollment ? (
            <div className="mfa-loading"><div className="mfa-spinner" /><p>Loading...</p></div>
          ) : status?.enrolled && !enrollment ? (
            /* ── MFA is enabled ──────────────────────── */
            <div className="mfa-status-card mfa-status-enabled">
              <div className="mfa-status-icon">🛡️</div>
              <div className="mfa-status-info">
                <div className="mfa-status-title">MFA is enabled</div>
                <div className="mfa-status-detail">
                  Your account is protected with two-factor authentication.
                  {status.factors?.[0]?.friendly_name && (
                    <> Device: <strong>{status.factors[0].friendly_name}</strong></>
                  )}
                </div>
              </div>
              <button
                className="mfa-btn mfa-btn-danger"
                onClick={() => handleRemove(status.factors[0].id)}
                disabled={removing}
              >
                {removing ? 'Removing...' : 'Disable MFA'}
              </button>
            </div>
          ) : enrollment ? (
            /* ── Enrollment in progress ───────────────── */
            <div className="mfa-enroll-card">
              <div className="mfa-enroll-step">
                <div className="mfa-step-number">1</div>
                <div className="mfa-step-text">
                  <strong>Scan this QR code</strong> with your authenticator app
                </div>
              </div>

              <div className="mfa-qr-wrap">
                {enrollment.qr && !qrFailed ? (
                  <img src={enrollment.qr} alt="QR code for your authenticator app" className="mfa-qr"
                    onError={() => setQrFailed(true)} />
                ) : (
                  <div className="mfa-qr-missing">
                    The QR code could not be shown. Add the account in your authenticator app
                    with the key below instead.
                  </div>
                )}
              </div>

              <div className="mfa-secret-section">
                <div className="mfa-secret-label">Can't scan? Enter this key manually:</div>
                <div className="mfa-secret-row">
                  <code className="mfa-secret-value" data-testid="mfa-secret">{groupSecret(enrollment.secret)}</code>
                  <button type="button" className="mfa-btn mfa-btn-ghost mfa-copy-btn" onClick={copySecret}>
                    {copied ? 'Copied' : 'Copy key'}
                  </button>
                </div>
              </div>

              <div className="mfa-enroll-step" style={{ marginTop: '24px' }}>
                <div className="mfa-step-number">2</div>
                <div className="mfa-step-text">
                  <strong>Enter the 6-digit code</strong> from your authenticator app to confirm
                </div>
              </div>

              <form onSubmit={handleVerify} className="mfa-verify-form">
                <input
                  type="text"
                  className="mfa-code-input"
                  placeholder="000000"
                  value={code}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 6)
                    setCode(v)
                  }}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                />
                <button
                  className="mfa-btn mfa-btn-primary"
                  type="submit"
                  disabled={code.length !== 6 || loading}
                >
                  {loading ? 'Verifying...' : 'Enable MFA'}
                </button>
              </form>

              <button
                className="mfa-btn mfa-btn-ghost"
                onClick={() => { setEnrollment(null); setCode(''); setError('') }}
                style={{ marginTop: '12px' }}
              >
                Cancel
              </button>
            </div>
          ) : (
            /* ── MFA not enrolled ────────────────────── */
            <div className="mfa-status-card mfa-status-disabled">
              <div className="mfa-status-icon">🔓</div>
              <div className="mfa-status-info">
                <div className="mfa-status-title">MFA is not enabled</div>
                <div className="mfa-status-detail">
                  Protect your account by requiring a verification code at sign-in.
                </div>
              </div>
              <button
                className="mfa-btn mfa-btn-primary"
                onClick={handleEnroll}
                disabled={enrolling}
              >
                {enrolling ? 'Setting up...' : 'Set Up MFA'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
